import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Plus, Edit, Trash2, Eye, EyeOff, LogOut } from "lucide-react";
import ProjectForm from "@/components/ProjectForm";
import AnalyticsCard from "@/components/AnalyticsCard";

type Project = {
  id: string;
  title: string;
  description: string;
  long_description?: string;
  image?: string;
  tags: string[];
  demo_url?: string;
  github_url?: string;
  is_school_project: boolean;
  color: 'yellow' | 'blue' | 'green' | 'pink';
  preview_images: string[];
  before_after_before?: string;
  before_after_after?: string;
  is_visible: boolean;
  display_order: number;
  display_type: 'carousel' | 'before_after' | 'video';
  created_at: string;
  updated_at: string;
};

const Admin = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [showForm, setShowForm] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    checkAuth();
    fetchProjects();
  }, []);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate('/admin-secret-access-7f8e9d2a1b3c4e5f6789abcdef012345');
      return false;
    }

    // Check if user has admin role
    const { data: hasAdminRole, error: roleError } = await supabase
      .rpc('has_admin_role', { user_id: session.user.id });

    if (roleError) {
      console.error('Error checking admin role:', roleError);
      toast({
        title: "Erreur d'autorisation",
        description: "Impossible de vérifier vos permissions.",
        variant: "destructive",
      });
      navigate('/');
      return false;
    }

    if (!hasAdminRole) {
      toast({
        title: "Accès refusé",
        description: "Vous n'avez pas les permissions d'administrateur.",
        variant: "destructive",
      });
      navigate('/');
      return false;
    }

    return true;
  };

  const fetchProjects = async () => {
    try {
      const { data, error } = await supabase
        .from("projects")
        .select("*")
        .order("display_order", { ascending: true });

      if (error) throw error;
      setProjects((data || []).map(project => ({
        ...project,
        color: project.color as 'yellow' | 'blue' | 'green' | 'pink',
        display_type: (project.display_type || 'carousel') as 'carousel' | 'before_after' | 'video'
      })));
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: "Impossible de charger les projets: " + error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  const toggleVisibility = async (project: Project) => {
    try {
      const { error } = await supabase
        .from("projects")
        .update({ is_visible: !project.is_visible })
        .eq("id", project.id);

      if (error) throw error;

      setProjects(projects.map(p => 
        p.id === project.id ? { ...p, is_visible: !p.is_visible } : p
      ));

      toast({
        title: "Succès",
        description: `Projet ${!project.is_visible ? "affiché" : "masqué"}`,
      });
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const deleteProject = async (id: string) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer ce projet ?")) return;

    try {
      const { error } = await supabase
        .from("projects")
        .delete()
        .eq("id", id);

      if (error) throw error;

      setProjects(projects.filter(p => p.id !== id));
      toast({
        title: "Succès",
        description: "Projet supprimé",
      });
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingProject(null);
    fetchProjects();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white" style={{ backgroundColor: '#141414' }}>
        <div>Chargement...</div>
      </div>
    );
  }

  if (showForm) {
    return (
      <ProjectForm
        project={editingProject}
        onSuccess={handleFormSuccess}
        onCancel={() => {
          setShowForm(false);
          setEditingProject(null);
        }}
        onBack={() => {
          setShowForm(false);
          setEditingProject(null);
        }}
      />
    );
  }

  return (
    <div className="min-h-screen text-white p-4 md:p-8" style={{ backgroundColor: '#141414' }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center mb-8 gap-4">
          <div>
            <h1 className="text-4xl md:text-5xl font-black mb-2 text-white">
              Administration
            </h1>
            <p className="text-gray-300 text-lg">Gérez vos projets et contenus</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button 
              variant="outline" 
              onClick={handleLogout}
              className="rounded-nobilito px-8 py-4 h-auto border-2 border-white text-white font-bold shadow-lg transition-all duration-300 hover:scale-105"
              style={{ backgroundColor: 'transparent' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'white';
                e.currentTarget.style.color = '#141414';
                e.currentTarget.style.borderColor = 'white';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = 'white';
                e.currentTarget.style.borderColor = 'white';
              }}
              size="lg"
            >
              <LogOut className="h-5 w-5 mr-2" />
              Déconnexion
            </Button>
          </div>
        </div>

        {/* Analytics Section */}
        <div className="mb-10">
          <h2 className="text-2xl font-black mb-4 text-white">Statistiques du site</h2>
          <AnalyticsCard />
        </div>

        {/* Projects Section */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h2 className="text-2xl font-black text-white">Mes projets ({projects.length})</h2>
          <Button 
            onClick={() => setShowForm(true)}
            className="rounded-nobilito font-bold px-6 py-3 h-auto bg-white text-black hover:bg-gray-100 shadow-lg transition-all duration-300 hover:scale-105 border-2 border-white"
            style={{ backgroundColor: '#111111', borderColor: '#111111', color: 'white' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
              e.currentTarget.style.color = '#111111';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#111111';
              e.currentTarget.style.color = 'white';
            }}
          >
            <Plus className="h-4 w-4 mr-2" />
            Nouveau projet
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {projects.map((project) => (
            <div 
              key={project.id} 
              className={`relative transition-all duration-300 transform hover:-translate-y-2 hover:shadow-2xl rounded-2xl border-0 shadow-xl ${project.color === 'yellow' ? 'shadow-accent/20' : project.color === 'blue' ? 'shadow-electric/20' : project.color === 'green' ? 'shadow-success/20' : 'shadow-pink/20'}`}
              style={{
                background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
                border: `2px solid ${
                  project.color === 'yellow' ? 'hsl(var(--accent))' :
                  project.color === 'blue' ? 'hsl(var(--electric))' :
                  project.color === 'green' ? 'hsl(var(--success))' :
                  'hsl(var(--pink))'
                }30`,
              }}
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-black mb-3 line-clamp-2 text-white">{project.title}</h3>
                    <div className="flex flex-wrap gap-2 mb-2">
                      <div 
                        className="px-3 py-1 rounded-full text-xs font-semibold"
                        style={{ 
                          backgroundColor: project.is_school_project ? '#2a2a2a' : '#1a4d2a',
                          color: project.is_school_project ? '#e5e7eb' : '#10b981'
                        }}
                      >
                        {project.is_school_project ? "École" : "Pro"}
                      </div>
                      <div 
                        className="px-3 py-1 rounded-full text-xs font-medium border"
                        style={{
                          borderColor: project.color === 'yellow' ? 'hsl(var(--accent))' :
                                     project.color === 'blue' ? 'hsl(var(--electric))' :
                                     project.color === 'green' ? 'hsl(var(--success))' :
                                     'hsl(var(--pink))',
                          backgroundColor: project.color === 'yellow' ? 'hsl(var(--accent) / 0.1)' :
                                         project.color === 'blue' ? 'hsl(var(--electric) / 0.1)' :
                                         project.color === 'green' ? 'hsl(var(--success) / 0.1)' :
                                         'hsl(var(--pink) / 0.1)',
                          color: project.color === 'yellow' ? 'hsl(var(--accent))' :
                               project.color === 'blue' ? 'hsl(var(--electric))' :
                               project.color === 'green' ? 'hsl(var(--success))' :
                               'hsl(var(--pink))'
                        }}
                      >
                        {project.color}
                      </div>
                      <div 
                        className="px-3 py-1 rounded-full text-xs font-medium"
                        style={{ 
                          backgroundColor: project.is_visible ? '#1a4d2a' : '#4d1a1a',
                          color: project.is_visible ? '#10b981' : '#ef4444'
                        }}
                      >
                        {project.is_visible ? "Visible" : "Masqué"}
                      </div>
                    </div>
                  </div>
                </div>
                
                <p className="text-sm text-gray-300 mb-4 line-clamp-3 leading-relaxed">
                  {project.description}
                </p>
                
                <div className="flex flex-wrap gap-2 mb-6">
                  {project.tags.slice(0, 3).map((tag) => (
                    <div key={tag} className="px-2 py-1 text-xs rounded-lg bg-gray-800/50 text-gray-300 border border-gray-700">
                      {tag}
                    </div>
                  ))}
                  {project.tags.length > 3 && (
                    <div className="px-2 py-1 text-xs rounded-lg bg-gray-800/50 text-gray-300 border border-gray-700">
                      +{project.tags.length - 3}
                    </div>
                  )}
                </div>
                
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="flex-1 font-bold rounded-nobilito text-white hover:text-white border-2 hover:border-white shadow-md transition-all duration-300 hover:scale-105"
                    style={{ backgroundColor: '#141414', borderColor: '#141414' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'white';
                      e.currentTarget.style.color = '#141414';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#141414';
                      e.currentTarget.style.color = 'white';
                    }}
                    onClick={() => {
                      setEditingProject(project);
                      setShowForm(true);
                    }}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Modifier
                  </Button>
                  <Button
                    size="sm"
                    className="px-3 rounded-nobilito text-white hover:text-white border-2 hover:border-white shadow-md transition-all duration-300 hover:scale-105"
                    style={{ backgroundColor: '#141414', borderColor: '#141414' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'white';
                      e.currentTarget.style.color = '#141414';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#141414';
                      e.currentTarget.style.color = 'white';
                    }}
                    onClick={() => toggleVisibility(project)}
                  >
                    {project.is_visible ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                  <Button
                    size="sm"
                    className="px-3 rounded-nobilito bg-red-600 text-white hover:bg-red-700 border-2 border-red-600 hover:border-red-700 shadow-md transition-all duration-300 hover:scale-105"
                    onClick={() => deleteProject(project.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {projects.length === 0 && (
          <div className="text-center py-16">
            <div className="max-w-md mx-auto">
              <div className="text-6xl mb-4 text-gray-500">Vide</div>
              <h3 className="text-xl font-black mb-2 text-gray-200">Aucun projet</h3>
              <p className="text-gray-300 mb-6">Commencez par créer votre premier projet pour voir apparaître vos réalisations ici.</p>
              <Button 
                onClick={() => setShowForm(true)}
                size="lg"
                className="rounded-nobilito px-8 py-4 h-auto font-bold text-white hover:text-white shadow-lg transition-all duration-300 hover:scale-105 border-2 hover:border-white"
                style={{ backgroundColor: '#141414', borderColor: '#141414' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'white';
                  e.currentTarget.style.color = '#141414';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#141414';
                  e.currentTarget.style.color = 'white';
                }}
              >
                <Plus className="h-5 w-5 mr-2" />
                Créer votre premier projet
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;